import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';
import { Booklet, Document, EDocumentType, Payment, Setting, User } from '@common/core/models';
import { DocumentsService } from '@common/core/services';
import { EBookletAttachmentName } from '@common/enums/attachments.enum';
import { ECostSummaryType } from '@common/enums/cost-summary-type.enum';
import { TCreatedPdf } from 'pdfmake/build/pdfmake';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { map, Observable, of } from 'rxjs';

export const SIGNATURE_PLACEHOLDER = 'firma';

export function getBookletContractDocuments({
  contractType,
  documentsService,
  booklet,
}: {
  contractType: 'casa-doc' | 'extra';
  documentsService: DocumentsService;
  booklet: Booklet;
}): Observable<Document[]> {
  let obs$: Observable<Document[]>;

  if (contractType === 'casa-doc') {
    const orderMap = [
      EDocumentType.SUMMARY_CONTRACT,
      EDocumentType.SURVEYOR_CONTRACT,
      EDocumentType.TECHNICAL_REPORT_CONTRACT,
      EDocumentType.REQUEST_FOR_DEED_OF_ORIGIN_CONTRACT,
      EDocumentType.APE_CONTRACT,
      EDocumentType.BOOKLET_DELEGATION_CONTRACT,
    ];
    const query = [];
    query.push({ type: EDocumentType.SUMMARY_CONTRACT });
    query.push({ type: EDocumentType.TECHNICAL_REPORT_CONTRACT });
    if (booklet.requestForApe) {
      query.push({ type: EDocumentType.APE_CONTRACT });
    }
    if (booklet.requestForDeedOfOrigin) {
      query.push({ type: EDocumentType.REQUEST_FOR_DEED_OF_ORIGIN_CONTRACT });
    }
    if (booklet.agent) {
      query.push({ type: EDocumentType.BOOKLET_DELEGATION_CONTRACT });
    }

    obs$ = documentsService
      .get<Document>({
        filter: { $or: query },
        page: 1,
        size: Number.MAX_SAFE_INTEGER,
      })
      .pipe(
        map((response) => response.content),
        map((documents) => documents.sort((a, b) => orderMap.indexOf(a.type) - orderMap.indexOf(b.type)))
      );
  } else if (contractType === 'extra') {
    obs$ = of([
      {
        name: 'Extra contract',
        type: null,
        file: null,
        html: booklet.extraContractHtml,
      } as Document,
    ]);
  }
  return obs$;
}

export function interpolateDocumentHtml({
  html,
  interpolationMap,
}: {
  html: string;
  interpolationMap: Record<string, string | number>;
}): string {
  let result = html;
  for (const key in interpolationMap) {
    result = result.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(interpolationMap[key] ?? ''));
  }
  return result;
}

/**
 * Processes conditional blocks in HTML templates.
 * Supports {{#if condition}}...{{/if}} and {{#if condition}}...{{else}}...{{/if}} syntax, also nested.
 * Conditions are resolved from the provided conditionsMap.
 */
export function processConditionalBlocks({
  html,
  conditionsMap,
}: {
  html: string;
  conditionsMap: Record<string, boolean>;
}): string {
  // Matches only the innermost blocks (no {{#if}} inside), so nested blocks are resolved from the inside out
  const innermostBlockRegex =
    /{{\s*#if\s+(\w+)\s*}}((?:(?!{{\s*#if\b)[\s\S])*?)(?:{{\s*else\s*}}((?:(?!{{\s*#if\b)[\s\S])*?))?{{\s*\/if\s*}}/g;

  let result = html;
  let previous: string;
  do {
    previous = result;
    result = result.replace(
      innermostBlockRegex,
      (_match, condition: string, ifContent: string, elseContent: string | undefined) =>
        conditionsMap[condition] ? ifContent : (elseContent ?? '')
    );
  } while (result !== previous);

  return result;
}

export function buildInterpolationMap({
  booklet,
  setting,
}: {
  booklet: Booklet;
  setting?: Setting;
}): Record<string, any> {
  return {
    // LIBRETTO
    id_libretto: booklet.id,

    // CONTRACT INFORMATION
    data_corrente: new Date().toLocaleDateString('it-IT'),
    nome: booklet.sellerName,
    cognome: booklet.sellerSurname,
    numero_di_telefono: booklet.sellerMobilePhone,
    email: booklet.sellerEmail,
    codice_fiscale: booklet.sellerFiscalCode,
    indirizzo_venditore: booklet.sellerAddress,
    indirizzo_immobile: booklet.propertyAddress,

    // COSTS
    prezzo_richiesta_atto_di_provenienza: booklet.requestForDeedOfOriginGrossPrice,
    prezzo_ape: booklet.apeGrossPrice,
    prezzo_relazione_tecnica: booklet.technicalReportGrossPrice,
    prezzo_extra: booklet.extraGrossPrice,
    prezzo_riepilogo_costi_venditore: booklet.bookletGrossPrice,
    prezzo_casadoc_relazione_tecnica:
      booklet.technicalReportGrossPrice - (setting?.technicalReportGrossPriceForSurveyor ?? 0),
    prezzo_default_tecnico_relazione_tecnica: setting?.technicalReportGrossPriceForSurveyor ?? 0,
    prezzo_casadoc_ape: booklet.apeGrossPrice - (setting?.apeGrossPriceForSurveyor ?? 0),
    prezzo_default_tecnico_ape: setting?.apeGrossPriceForSurveyor ?? 0,
    prezzo_atto_di_provenienza: booklet.requestForDeedOfOriginGrossPrice ?? 0,
    prezzo_extra_tecnico: booklet.paymentsByRole?.extra?.grossPrice ?? 0,
    prezzo_extra_tecnico_no_diritti_segreteria:
      (booklet.paymentsByRole?.extra?.grossPrice ?? 0) - (booklet.extraCost ?? 0),
    prezzo_extra_diritti_segreteria: booklet.extraCost ?? 0,
    prezzo_casadoc_extra: (booklet.extraCasaDocGrossPrice ?? 0) + (booklet.extraAccountGrossPrice ?? 0),

    // COULD BE USED IN THE FUTURE
    data_ultimi_lavori: booklet.propertyLastWorkData?.toLocaleDateString('it-IT'),
    descrizione_immobile: booklet.propertyDescription,
    data_rogito: booklet.propertyDeedOfSale?.toLocaleDateString('it-IT'),
    stato_libretto: booklet.bookletStatus,
    prezzo_accesso_agli_atti: booklet.technicalReportCost,

    // AGENT
    agente_nome: booklet.agent?.name,
    agente_cognome: booklet.agent?.surname,
    agente_email: booklet.agent?.email,
    agente_numero_di_telefono: booklet.agent?.phoneNumber,

    richiesta_ape: booklet.requestForApe,
    richiesta_atto_di_provenienza: booklet.requestForDeedOfOrigin,
    richiesta_relazione_tecnica: booklet.requestForTechnicalReport,
    richiesta_extra: booklet.extraSurveyor ? true : false,
  };
}

type TCostSummaryHtmlField = 'costSummaryHtml' | 'extraCostSummaryHtml' | 'unifiedCostSummaryHtml';
type TCostSummarySendDateField = 'bookletProformaInvoiceDate' | 'extraBookletProformaInvoiceDate';

// Same policy of the proforma: technical report and extra can be summarised together or separately.
// The owner sees at most two documents, so the unified version replaces the separated ones and vice versa.
export const COST_SUMMARY_CONFIG: Record<
  ECostSummaryType,
  {
    labelKey: string;
    attachmentLabelKey: string;
    htmlField: TCostSummaryHtmlField;
    attachmentName: EBookletAttachmentName;
    replacedAttachmentNames: EBookletAttachmentName[];
    sendDateFields: TCostSummarySendDateField[];
    fileNameSuffix: string;
  }
> = {
  [ECostSummaryType.TECHNICAL_REPORT]: {
    labelKey: bitfToTranslate('COMMON.BOOKLET.COST_SUMMARY_TECHNICAL_REPORT'),
    attachmentLabelKey: bitfToTranslate('BOOKLET_ATTACHMENT.NAME.COST_SUMMARY'),
    htmlField: 'costSummaryHtml',
    attachmentName: EBookletAttachmentName.COST_SUMMARY,
    replacedAttachmentNames: [EBookletAttachmentName.UNIFIED_COST_SUMMARY],
    sendDateFields: ['bookletProformaInvoiceDate'],
    fileNameSuffix: 'riepilogo_costi',
  },
  [ECostSummaryType.EXTRA]: {
    labelKey: bitfToTranslate('COMMON.BOOKLET.COST_SUMMARY_EXTRA'),
    attachmentLabelKey: bitfToTranslate('BOOKLET_ATTACHMENT.NAME.EXTRA_COST_SUMMARY'),
    htmlField: 'extraCostSummaryHtml',
    attachmentName: EBookletAttachmentName.EXTRA_COST_SUMMARY,
    replacedAttachmentNames: [EBookletAttachmentName.UNIFIED_COST_SUMMARY],
    sendDateFields: ['extraBookletProformaInvoiceDate'],
    fileNameSuffix: 'riepilogo_costi_extra',
  },
  [ECostSummaryType.UNIFIED]: {
    labelKey: bitfToTranslate('COMMON.BOOKLET.COST_SUMMARY_UNIFIED'),
    attachmentLabelKey: bitfToTranslate('BOOKLET_ATTACHMENT.NAME.UNIFIED_COST_SUMMARY'),
    htmlField: 'unifiedCostSummaryHtml',
    attachmentName: EBookletAttachmentName.UNIFIED_COST_SUMMARY,
    replacedAttachmentNames: [EBookletAttachmentName.COST_SUMMARY, EBookletAttachmentName.EXTRA_COST_SUMMARY],
    sendDateFields: ['bookletProformaInvoiceDate', 'extraBookletProformaInvoiceDate'],
    fileNameSuffix: 'riepilogo_costi',
  },
};

function formatCostSummaryAmount(value: number | null | undefined, isIncluded: boolean): string {
  if (!isIncluded || value === null || value === undefined) {
    return '';
  }
  return value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getUserFullName(user: User | undefined, isIncluded: boolean): string {
  if (!isIncluded || !user) {
    return '';
  }
  return [user.name, user.surname].filter(Boolean).join(' ');
}

function formatPercentage(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

// Explains what the surveyor amount includes (same rule of getUserPayment in Strapi booklet-finance):
// cash fund is always applied when set, VAT only for surveyors with VAT
function getSurveyorPriceNote(payment: Payment | undefined, setting: Setting | undefined): string {
  const user = payment?.user;
  if (!user) {
    return '';
  }

  const cashLabel = user.cashPercentage ? `cassa ${formatPercentage(user.cashPercentage)}%` : '';
  if (user.hasVat) {
    const vatLabel = `IVA ${formatPercentage(setting?.vatPercentage)}%`;
    return cashLabel ? `(${vatLabel} e ${cashLabel} incluse)` : `(${vatLabel} inclusa)`;
  }
  return cashLabel ? `(${cashLabel} inclusa, non soggetto a IVA)` : '(non soggetto a IVA)';
}

// One revenue stamp for each surveyor without VAT (same rule of booklet-finance in Strapi),
// charged on the first service of the surveyor in the summary
function getSurveyorRevenueStamp(
  payment: Payment | undefined,
  setting: Setting | undefined,
  chargedUserIds: Set<string>
): number {
  const user = payment?.user;
  if (!user || user.hasVat || chargedUserIds.has(user.documentId)) {
    return 0;
  }
  chargedUserIds.add(user.documentId);
  return setting?.revenueStamp ?? 0;
}

function formatSignatureDate(date: Date | string | undefined): string {
  return date ? ` firmato il ${new Date(date).toLocaleDateString('it-IT')}` : '';
}

// The extra contract is always a separate contract, so the unified summary mentions both
function getCostSummaryContractsReference(
  booklet: Booklet,
  includesTechnicalReport: boolean,
  includesExtra: boolean
): string {
  const references: string[] = [];
  if (includesTechnicalReport) {
    references.push(`nel contratto Casadoc${formatSignatureDate(booklet.casaDocContractSignatureDate)}`);
  }
  if (includesExtra) {
    // Booklets signed before the signature date was tracked fall back to the signed file upload date
    const extraContractDate =
      booklet.extraContractSignatureDate ??
      booklet.bookletAttachments?.[EBookletAttachmentName.EXTRA_CONTRACT]?.file?.[0]?.createdAt;
    references.push(`nel contratto extra${formatSignatureDate(extraContractDate)}`);
  }
  return references.join(' e ');
}

export function buildCostSummaryInterpolationMap({
  booklet,
  setting,
  type,
}: {
  booklet: Booklet;
  setting?: Setting;
  type: ECostSummaryType;
}): Record<string, any> {
  // Same sections and amounts of the admin "Riepilogo Costi Venditore"
  // (admin-booklet-riepilogo-costi-venditore component)
  const payments = booklet.paymentsByRole;
  const includesTechnicalReport = type !== ECostSummaryType.EXTRA;
  const includesApe = includesTechnicalReport && !!booklet.requestForApe && !!payments?.ape;
  const includesDeedOfOrigin = includesTechnicalReport && !!booklet.requestForDeedOfOrigin;
  const includesExtra = type !== ECostSummaryType.TECHNICAL_REPORT && !!payments?.extra;
  // Order matters: the revenue stamp is charged on the first service of each surveyor
  const chargedUserIds = new Set<string>();
  const technicalReportRevenueStamp = includesTechnicalReport
    ? getSurveyorRevenueStamp(payments?.technicalReport, setting, chargedUserIds)
    : 0;
  const apeRevenueStamp = includesApe ? getSurveyorRevenueStamp(payments?.ape, setting, chargedUserIds) : 0;
  const extraRevenueStamp = includesExtra
    ? getSurveyorRevenueStamp(payments?.extra, setting, chargedUserIds)
    : 0;

  // Each service total includes the revenue stamp of its surveyor
  const technicalReportTotal = (booklet.technicalReportGrossPrice ?? 0) + technicalReportRevenueStamp;
  const apeTotal = (booklet.apeGrossPrice ?? 0) + apeRevenueStamp;
  const deedOfOriginTotal =
    (booklet.requestForDeedOfOriginGrossPrice ?? 0) + (booklet.requestForDeedOfOriginCostGrossPrice ?? 0);
  const extraTotal = (booklet.extraGrossPrice ?? 0) + extraRevenueStamp;
  const extraSecretaryFees = booklet.extraCost ?? 0;

  // The unified version uses the booklet totals, the separated ones only the services they include
  const total =
    type === ECostSummaryType.UNIFIED
      ? (booklet.bookletGrossPrice ?? 0) + (booklet.revenueStampTotal ?? 0)
      : (includesTechnicalReport ? technicalReportTotal : 0) +
        (includesApe ? apeTotal : 0) +
        (includesDeedOfOrigin ? deedOfOriginTotal : 0) +
        (includesExtra ? extraTotal : 0);

  return {
    ...buildInterpolationMap({ booklet, setting }),

    numero_libretto: `${booklet.id}/${new Date().getFullYear()}`,
    percentuale_iva: formatPercentage(setting?.vatPercentage),
    riferimento_contratti: getCostSummaryContractsReference(booklet, includesTechnicalReport, includesExtra),

    // TECHNICAL REPORT
    riepilogo_relazione_tecnica_totale: formatCostSummaryAmount(
      technicalReportTotal,
      includesTechnicalReport
    ),
    riepilogo_relazione_tecnica_casadoc: formatCostSummaryAmount(
      booklet.technicalReportCasaDocGrossPrice,
      includesTechnicalReport
    ),
    riepilogo_relazione_tecnica_tecnico: formatCostSummaryAmount(
      payments?.technicalReport?.grossPrice,
      includesTechnicalReport
    ),
    tecnico_relazione_tecnica: getUserFullName(booklet.technicalReportSurveyor, includesTechnicalReport),
    nota_tecnico_relazione_tecnica: includesTechnicalReport
      ? getSurveyorPriceNote(payments?.technicalReport, setting)
      : '',
    marca_da_bollo_relazione_tecnica: formatCostSummaryAmount(technicalReportRevenueStamp, true),

    // APE
    riepilogo_ape_totale: formatCostSummaryAmount(apeTotal, includesApe),
    riepilogo_ape_casadoc: formatCostSummaryAmount(booklet.apeCasaDocGrossPrice, includesApe),
    riepilogo_ape_tecnico: formatCostSummaryAmount(payments?.ape?.grossPrice, includesApe),
    tecnico_ape: getUserFullName(booklet.apeSurveyor, includesApe),
    nota_tecnico_ape: includesApe ? getSurveyorPriceNote(payments?.ape, setting) : '',
    marca_da_bollo_ape: formatCostSummaryAmount(apeRevenueStamp, true),

    // DEED OF ORIGIN
    riepilogo_atto_di_provenienza_totale: formatCostSummaryAmount(deedOfOriginTotal, includesDeedOfOrigin),
    riepilogo_atto_di_provenienza_casadoc: formatCostSummaryAmount(
      booklet.requestForDeedOfOriginGrossPrice,
      includesDeedOfOrigin
    ),
    riepilogo_atto_di_provenienza_costi_recupero: formatCostSummaryAmount(
      booklet.requestForDeedOfOriginCostGrossPrice ?? 0,
      includesDeedOfOrigin
    ),

    // EXTRA: the surveyor gross price includes the secretary fees (without VAT), shown on their own line
    riepilogo_extra_totale: formatCostSummaryAmount(extraTotal, includesExtra),
    riepilogo_extra_casadoc: formatCostSummaryAmount(booklet.extraCasaDocGrossPrice ?? 0, includesExtra),
    riepilogo_extra_tecnico: formatCostSummaryAmount(
      (payments?.extra?.grossPrice ?? 0) - extraSecretaryFees,
      includesExtra
    ),
    riepilogo_extra_diritti_segreteria: formatCostSummaryAmount(extraSecretaryFees, includesExtra),
    tecnico_extra: getUserFullName(booklet.extraSurveyor, includesExtra),
    nota_tecnico_extra: includesExtra ? getSurveyorPriceNote(payments?.extra, setting) : '',
    marca_da_bollo_extra: formatCostSummaryAmount(extraRevenueStamp, true),

    // TOTALS
    riepilogo_totale: formatCostSummaryAmount(total, true),

    // CONDITIONS
    include_relazione_tecnica: includesTechnicalReport,
    include_ape: includesApe,
    include_atto_di_provenienza: includesDeedOfOrigin,
    include_extra: includesExtra,
    ha_diritti_segreteria_extra: includesExtra && extraSecretaryFees > 0,
    ha_marca_da_bollo_relazione_tecnica: technicalReportRevenueStamp > 0,
    ha_marca_da_bollo_ape: apeRevenueStamp > 0,
    ha_marca_da_bollo_extra: extraRevenueStamp > 0,
  };
}

// The template is stored with a WYSIWYG editor (CKEditor in Strapi) that may wrap the conditional markers
// in their own paragraph or list item and split a list around them: markers are unwrapped before
// processing and the split lists are joined back after, so no empty lines or detached items are left.
const CONDITIONAL_MARKER_PATTERN = String.raw`{{\s*(?:#if\s+\w+|else|\/if)\s*}}`;
const BLANK_PATTERN = String.raw`(?:\s|&nbsp;|<br\s*\/?>)*`;
// A paragraph or list item (optionally with an inner paragraph) containing only one or more markers
const WRAPPED_CONDITIONAL_MARKERS_REGEX = new RegExp(
  `<(p|li)[^>]*>${BLANK_PATTERN}(?:<p[^>]*>)?${BLANK_PATTERN}((?:${CONDITIONAL_MARKER_PATTERN}${BLANK_PATTERN})+)(?:</p>)?${BLANK_PATTERN}</\\1>`,
  'g'
);
const CONDITIONAL_MARKER_REGEX = new RegExp(CONDITIONAL_MARKER_PATTERN, 'g');
// Paragraphs and list items left empty once the conditional blocks are removed
const EMPTY_BLOCK_REGEX = new RegExp(
  `<(p|li)[^>]*>(?:${BLANK_PATTERN}|<p[^>]*>${BLANK_PATTERN}</p>)*</\\1>`,
  'g'
);
const SPLIT_LIST_REGEX = /<\/(ul|ol)>\s*<\1[^>]*>/g;

export function buildCostSummaryHtml({
  html,
  interpolationMap,
}: {
  html: string;
  interpolationMap: Record<string, any>;
}): string {
  const unwrappedHtml = html.replace(WRAPPED_CONDITIONAL_MARKERS_REGEX, (_match, _tag, markers: string) =>
    (markers.match(CONDITIONAL_MARKER_REGEX) ?? []).join('')
  );
  const result = processConditionalBlocks({ html: unwrappedHtml, conditionsMap: interpolationMap })
    .replace(EMPTY_BLOCK_REGEX, '')
    .replace(SPLIT_LIST_REGEX, '');

  return interpolateDocumentHtml({ html: result, interpolationMap });
}

export function buildContractHtmlFromTemplates({
  documents,
  interpolationMap,
  signatureReplace,
}: {
  documents: Document[];
  interpolationMap: Record<string, any>;
  signatureReplace: string;
}): string {
  let template = '';
  documents.forEach((document, index) => {
    if (index > 0) {
      template += '\n<div class="page-break mt-5 pb-3"></div>\n';
    }
    template += document.html;
  });

  template = processConditionalBlocks({
    html: template,
    conditionsMap: interpolationMap,
  });

  template = interpolateDocumentHtml({
    html: template,
    interpolationMap,
  });

  // @NOTE: force to add space after page-break divs to allow be rendered correctly on the pdf
  template = template.replace(
    /<div([^>]*class="[^"]*page-break[^"]*"[^>]*)>(.*?)<\/div>/g,
    (_match, attributes, content) => {
      const newContent = content.trim() ? `${content}&nbsp;&nbsp;` : `&nbsp;&nbsp;`;
      return `<div${attributes}>${newContent}</div>`;
    }
  );

  template = template.replace(new RegExp(`{{${SIGNATURE_PLACEHOLDER}}}`, 'g'), signatureReplace);
  template = template.replace(/>\s+</g, '><');

  return template;
}
const PDF_LOGO_URL = '/assets/images/logo_casadoc.svg';

async function getPdfLogoContent(hasLogo: boolean): Promise<Content[]> {
  if (!hasLogo) {
    return [];
  }
  const svg = await fetch(PDF_LOGO_URL).then((response) => response.text());
  return [{ svg, width: 100, margin: [0, 0, 0, 10] }];
}

export async function generatePdfDocument({
  html,
  fileName,
  hasLogo = false,
}: {
  html: string;
  fileName: string;
  hasLogo?: boolean;
}): Promise<TCreatedPdf> {
  const [pdfMakeModule, htmlToPdfmakeModule, pdfFontsModule, logoContent] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('html-to-pdfmake'),
    import('@assets/pdf-make-fonts.js'),
    getPdfLogoContent(hasLogo),
  ]);
  const pdfMake = pdfMakeModule.default;
  const htmlToPdfmake = htmlToPdfmakeModule.default;
  const pdfFonts = pdfFontsModule.default;

  // Configure fonts
  pdfMake.vfs = pdfFonts.pdfMake.vfs;

  const docDefinition = {
    content: [
      ...logoContent,
      htmlToPdfmake(html, {
        removeExtraBlanks: true,
        ignoreStyles: ['font-family'],
      }),
    ],
    pageSize: 'A4',
    pageOrientation: 'portrait',
    language: 'it-IT',
    info: {
      title: fileName,
      author: 'Casadoc',
      creator: 'Casadoc',
      producer: 'Casadoc',
    },
    pageBreakBefore: (node) => node.style && (node.style as string[]).includes('page-break'),
    defaultStyle: {
      lineHeight: 1.2,
      fontSize: 11,
      font: 'Arial',
    },
  } as TDocumentDefinitions;

  const pdf = pdfMake.createPdf(docDefinition, null, {
    Arial: {
      normal: 'ARIAL.TTF',
      bold: 'ARIALBD.TTF',
      italics: 'ARIALI.TTF',
      bolditalics: 'ARIALBI.TTF',
    },
  });

  return pdf;
}
